import React from 'react';
import { PublicProfile } from '../../types/profile';
import { useTranslation } from 'react-i18next';

interface ProfileHeaderProps {
    profile: PublicProfile;
    isOwnProfile: boolean;
    onFollow?: () => void;
    onUnfollow?: () => void;
    onMessage?: () => void;
    loading?: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
    profile,
    isOwnProfile,
    onFollow,
    onUnfollow,
    onMessage,
    loading = false,
}) => {
    const { t } = useTranslation('profile');

    const displayName = profile.fullName || profile.username;
    const initial = displayName.charAt(0).toUpperCase();

    return (
        <div className="profile-header">
            <div className="profile-header__avatar">
                {profile.avatarUrl ? (
                    <img
                        src={profile.avatarUrl}
                        alt={displayName}
                    />
                ) : (
                    <span className="profile-header__avatar-initial">{initial}</span>
                )}
            </div>

            <div className="profile-header__body">
                <div className="profile-header__name-row">
                    <h1 className="profile-header__name">{displayName}</h1>
                    {profile.hasPublishedCourses && (
                        <span className="profile-header__badge">
                            {t('creator.badge', 'Creator')}
                        </span>
                    )}
                </div>

                <p className="profile-header__handle">@{profile.username}</p>

                {profile.bio && (
                    <p className="profile-header__bio">{profile.bio}</p>
                )}

                <div className="profile-header__stats">
                    <div className="profile-header__stat">
                        <span className="profile-header__stat-value">
                            {profile.followerCount}
                        </span>
                        <span className="profile-header__stat-label">
                            {t('followers.label', 'Followers')}
                        </span>
                    </div>
                    <span className="profile-header__stat-divider" aria-hidden="true" />
                    <div className="profile-header__stat">
                        <span className="profile-header__stat-value">
                            {profile.followingCount}
                        </span>
                        <span className="profile-header__stat-label">
                            {t('following.label', 'Following')}
                        </span>
                    </div>
                </div>

                {!isOwnProfile && (
                    <div className="profile-header__actions">
                        {profile.isFollowing ? (
                            <button
                                onClick={onUnfollow}
                                disabled={loading}
                                className="profile-header__btn profile-header__btn--primary"
                            >
                                {t('unfollow', 'Unfollow')}
                            </button>
                        ) : (
                            <button
                                onClick={onFollow}
                                disabled={loading}
                                className="profile-header__btn profile-header__btn--primary"
                            >
                                {t('follow', 'Follow')}
                            </button>
                        )}
                        <button
                            onClick={onMessage}
                            className="profile-header__btn profile-header__btn--secondary"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                            {t('message', 'Message')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileHeader;
